import {
  faArrowLeft,
  faArrowRight,
  faHome,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAsync } from 'react-use';
import { BookApi } from '../API/BookApi';
import { useAuth } from '../Auth/AuthProvider';
import LoadingIcon from '../Components/LoadingIcon';
import ReadArticleView from '../ReadArticle/ReadArticleView';
import { ArticleStatus } from '../Types/Article';
import { BookChapterContextProvider } from './Context/BookChapterContextProvider';

const bookApi = new BookApi();

const ReadBookView: React.FC = () => {
  const { bookId, chapterId } = useParams<{
    bookId: string;
    chapterId?: string;
  }>();
  const { userId } = useAuth();

  const { value: book, loading: isBookLoading } = useAsync(async () => {
    return await bookApi.getBook(bookId);
  }, [bookId]);

  const chapter = useMemo(() => {
    if (chapterId) {
      return book?.chapters.find((chapter) => chapter.id === chapterId);
    }

    // Show the first chapter if no chapterId is provided
    return book?.chapters?.sort((a, b) => a.chapterNumber - b.chapterNumber)[0];
  }, [book, chapterId]);

  const { previousChapter, nextChapter } = useMemo(() => {
    if (!book || !chapter) return {};
    const sortedChapters = book.chapters
      .filter((c) => c.article?.status === ArticleStatus.ACTIVE)
      .sort((a, b) => a.chapterNumber - b.chapterNumber);

    const currentIndex = sortedChapters.findIndex((c) => c.id === chapter.id);
    return {
      previousChapter:
        currentIndex > 0 ? sortedChapters[currentIndex - 1] : null,
      nextChapter:
        currentIndex < sortedChapters.length - 1
          ? sortedChapters[currentIndex + 1]
          : null,
    };
  }, [book, chapter]);

  const isInitialized = !isBookLoading && !!chapter;

  if (!isInitialized) {
    return <LoadingIcon fullScreen />;
  }

  return (
    <div>
      <div className="flex justify-between items-center ml-4 mr-4 mt-4">
        <div className="flex items-center gap-4">
          <Link
            to={`/book/${bookId}`}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <FontAwesomeIcon icon={faHome} className="mr-2" />
            Book Home
          </Link>

          {previousChapter && previousChapter.canReadChapter(userId) && (
            <Link
              to={`/book/${bookId}/${previousChapter.id}`}
              className="flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              Chapter {previousChapter.chapterNumber}
            </Link>
          )}
        </div>

        {nextChapter && nextChapter.canReadChapter(userId) && (
          <Link
            to={`/book/${bookId}/${nextChapter.id}`}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 ml-auto"
          >
            Chapter {nextChapter.chapterNumber}
            <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
          </Link>
        )}
      </div>
      <BookChapterContextProvider book={book} chapter={chapter}>
        <ReadArticleView articleId={chapter.articleId} />
      </BookChapterContextProvider>
    </div>
  );
};

export default ReadBookView;
